/**
 * Single source of truth for the timing constants used by the
 * `/build-game` interview's lock model and the `<InterviewInProgressBadge>`
 * Dashboard pill. Both surfaces care about the same thresholds; expressing
 * them once here prevents drift.
 *
 * Codex round 4 (PR #149) flagged the original draft's use of independent
 * literal `30 * 60 * 1000` values in the badge component AND the lock
 * guard as a maintenance hazard — if a future tweak touches one without
 * the other, the renderer's "interview waiting" cutover stops aligning
 * with the main process's takeover decision.
 *
 * Two separate constants for `STALE_PROGRESS_THRESHOLD_MS` and
 * `STALE_LOCKED_THRESHOLD_MS` even though they currently share a value:
 * the *meanings* differ (one is "skill made forward motion", the other
 * is "renderer / CLI is alive"), and a future calibration may want them
 * decoupled. Keep them as separate exports so the call sites read
 * intent rather than a magic 30.
 *
 * Both main and renderer import from this module directly; nothing
 * else.
 */
/**
 * How long a `progressAt` timestamp can stay unchanged before the
 * interview is considered stalled. Used by:
 *
 * - `<InterviewInProgressBadge>` to decide between the sky-blue
 *   "in progress" pill and the amber "interview waiting" pill.
 * - `concurrent-session guard` to decide whether a session whose
 *   renderer is still ticking but whose skill hasn't moved is
 *   "noisy-stuck" and a takeover should default to yes.
 *
 * 30 minutes is the conservative upper bound — a real user thinking
 * deeply about a question is rarely past it; a wedged skill rarely
 * recovers under it.
 */
export const STALE_PROGRESS_THRESHOLD_MS = 30 * 60 * 1000;
/**
 * How long `lockedAt` can age before the lock is broken silently
 * without prompting the user. Renderer (and CLI) sessions that are
 * still alive heartbeat well under this. Past this, the holding
 * session has almost certainly been killed (Foundry quit, machine
 * slept across the threshold, terminal closed).
 */
export const STALE_LOCKED_THRESHOLD_MS = 30 * 60 * 1000;
/**
 * Shorter "noisy-stuck" threshold. When `lockedAt` is fresh
 * (renderer still heartbeating) but `progressAt` is older than this,
 * the holding session's skill is wedged. The takeover prompt
 * defaults to "take over" rather than refusing without confirmation.
 */
export const NOISY_STUCK_THRESHOLD_MS = 5 * 60 * 1000;
/**
 * Renderer-side heartbeat cadence. A Foundry Workspace open and
 * waiting on user input bumps `lockedAt` every this many ms so a
 * user mid-question for several minutes doesn't spuriously age out
 * their own lock. The heartbeat does NOT touch `progressAt` — that
 * remains "real forward motion only" (answer recorded, outbox
 * drained, status transitioned).
 */
export const RENDERER_HEARTBEAT_MS = 60 * 1000;
/**
 * Schema version stamped into every persisted
 * `build-game-interview.json` file so future migrations can branch on
 * it. Bumped only when the on-disk shape changes in a backwards-
 * incompatible way; additive fields don't bump.
 */
export const BUILD_GAME_INTERVIEW_SCHEMA_VERSION = 1;
//# sourceMappingURL=build-game-constants.js.map